import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ImageOption } from '@/types/quiz';
import { Upload, X, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ImageOptionUploadProps {
  options: ImageOption[];
  correctAnswer: number;
  questionId: string;
  onChange: (options: ImageOption[], correctAnswer: number) => void;
}

export const ImageOptionUpload = ({ 
  options, 
  correctAnswer, 
  questionId,
  onChange 
}: ImageOptionUploadProps) => {
  const [uploading, setUploading] = useState<number | null>(null);

  const handleImageUpload = async (index: number, file: File) => {
    try {
      setUploading(index);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/questions/${questionId}/option-${index}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('quiz-images')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('quiz-images')
        .getPublicUrl(filePath);

      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], imageUrl: publicUrl };
      onChange(newOptions, correctAnswer);

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(null);
    }
  };

  const handleLabelChange = (index: number, label: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], label };
    onChange(newOptions, correctAnswer);
  };

  const handleRemoveImage = async (index: number) => {
    const imageUrl = options[index].imageUrl;
    if (imageUrl) {
      try {
        const pathMatch = imageUrl.match(/quiz-images\/(.+)$/);
        if (pathMatch) {
          await supabase.storage.from('quiz-images').remove([pathMatch[1]]);
        }
      } catch (error) {
        console.error('Failed to remove image:', error);
      }
    }

    const newOptions = [...options];
    newOptions[index] = { imageUrl: '', label: newOptions[index].label || '' };
    onChange(newOptions, correctAnswer);
  };

  const handleSetCorrect = (index: number) => {
    onChange(options, index);
  };

  const handleAddOption = () => {
    if (options.length < 6) {
      onChange([...options, { imageUrl: '', label: '' }], correctAnswer);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      const newCorrectAnswer = correctAnswer >= index && correctAnswer > 0 
        ? correctAnswer - 1 
        : correctAnswer;
      onChange(newOptions, newCorrectAnswer);
    }
  };

  return (
    <div className="space-y-4">
      <Label>Image Options (select the correct answer)</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.map((option, index) => (
          <Card key={index} className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label>Option {index + 1}</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={correctAnswer === index ? 'default' : 'outline'}
                  onClick={() => handleSetCorrect(index)}
                  className="gap-1"
                >
                  {correctAnswer === index && <Check className="h-3 w-3" />}
                  Correct
                </Button>
                {options.length > 2 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {option.imageUrl ? (
              <div className="space-y-2">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                  <img
                    src={option.imageUrl}
                    alt={option.label || `Option ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => handleRemoveImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(index, file);
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploading === index}
                />
                <div className="aspect-video rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center gap-2 bg-muted/50 hover:bg-muted/80 transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {uploading === index ? 'Uploading...' : 'Click to upload image'}
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Label (optional)</Label>
              <Input
                type="text"
                placeholder="Optional text label"
                value={option.label || ''}
                onChange={(e) => handleLabelChange(index, e.target.value)}
              />
            </div>
          </Card>
        ))}
      </div>

      {options.length < 6 && (
        <Button 
          type="button" 
          variant="outline" 
          onClick={handleAddOption}
          className="w-full"
        >
          Add Image Option
        </Button>
      )}
    </div>
  );
};
